import React, { useState } from 'react';

import { SelectChangeEvent } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  ContentTemplate,
  CustomSelect,
  Divider,
  Grid,
  HtmlTrans,
  SectionHeader,
  Typography,
} from 'web-library';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useUserDetails } from '../../../../hooks/userDetails';
import { candidateService } from '../../../../api';
import {
  AppealAvailableLevelResponse,
  AppealSelectSubjectAppealParams,
} from '../../../../api/candidate/appeal/model';
import { Role } from '../../../../models';
import RouteKey from '../../../../routes/key';
import ClosedOpenWindowCard from '../../../../components/cards/ClosedOpenWindowCard';
import { usePostTransactionLog } from '../../../../api-refactored/candidate/usePostTransactionLog';
import { parseLocationSearch } from '../../../../utils/StringUtils';
import { useWOGAA } from '../../../../hooks/wogaa';

const { appeal } = RouteKey.child;
const { selectSubject, applications } = RouteKey.appeal.child;

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  notesContainer: {
    paddingBottom: theme.spacing(2),
  },
  examLevelTable: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  startAppealButton: {
    paddingTop: theme.spacing(4),
    marginBottom: theme.spacing(7),
  },
  feeContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: 'red',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  feeColumnHeader: {
    textAlign: 'right',
  },
  divider: {
    marginTop: theme.spacing(4),
  },
  sectionHeader: {
    marginBottom: theme.spacing(4),
  },
  examLevelSelect: {}
}));

const examLevelMenuOptions = (examLevels: AppealAvailableLevelResponse) =>
  examLevels.map((examLevel, index) => ({
    value: index,
    key: index,
    children: examLevel.examLevel,
  }));

const Notes = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { token = '' } = parseLocationSearch(location.search);

  const [selectedExamLevelIndex, setSelectedExamLevelIndex] = useState<number | undefined>(0);

  const { data: userDetails } = useUserDetails();
  const { data: appealSummaryData } = candidateService.appeal.candidate.useGetAppealFees();
  const {
    data: availableExamLevelResults,
  } = candidateService.appeal.candidate.useGetAvailableResult(token);

  const { mutate: apiMutation, status } = usePostTransactionLog({
    onSuccess: () => {
      const selectSubjectParams = getSelectSubjectParams();
      history.push(
        `/${appeal}/${applications.route}/${selectSubject.route}/?${selectSubjectParams}`
      );
    },
  });

  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    // @ts-ignore
    setSelectedExamLevelIndex(e.target.value);
  };

  const getSelectSubjectParams = () => {
    if (availableExamLevelResults && selectedExamLevelIndex !== undefined) {
      const selectedExamLevel = availableExamLevelResults[selectedExamLevelIndex];
      const selectSubjectQueryParams: AppealSelectSubjectAppealParams = {
        series: selectedExamLevel.examSeriesCode,
        year: selectedExamLevel.examYear,
        code: selectedExamLevel.examCode,
        token,
      };
      return queryString.stringify(selectSubjectQueryParams);
    }
    return null;
  };

  const { transactionStart } = useWOGAA('myexamsseab-6473');
  const handleClick = () => {
    const selectSubjectParams = getSelectSubjectParams();

    if (selectSubjectParams) {
      transactionStart();
      apiMutation({
        auditActionType: 'START_APPEAL',
        applicationId: null,
      });
    }
  };

  if (availableExamLevelResults && availableExamLevelResults.length <= 0) {
    return (
      <ClosedOpenWindowCard
        title={t('appeal->>AppealNotes.AppealTitle')}
        contentBody={t('appeal->>AppealNotes.AppealUnavailable')}
      />
    );
  }

  return (
    <>
      {userDetails &&
        userDetails?.data.roles?.includes(Role.candidate) &&
        appealSummaryData &&
        availableExamLevelResults && (
          <ContentTemplate>
            <Grid>
              <div className={classes.notesContainer}>
                <SectionHeader title={t('appeal->>AppealNotes.ImportantNotesForCandidates')} />
                <ul>
                  <li>
                    <Typography variant="body1">
                      {t('appeal->>AppealNotes.CandidateAppealNotesStatementOne')}
                    </Typography>
                    <br />
                    <Typography variant="body1">
                      {t('appeal->>AppealNotes.CandidateAppealNotesStatementTwo')}
                    </Typography>
                  </li>
                  <br />
                  <li>
                    <Typography>
                      {t('appeal->>AppealNotes.CandidateAppealNotesStatementThree')}
                    </Typography>
                  </li>
                </ul>
                <ul>
                  <li>
                    <Typography>
                      {t('appeal->>AppealNotes.CandidateAppealNotesStatementFour')}
                    </Typography>
                  </li>
                </ul>
                <Typography variant="body1">
                  <HtmlTrans i18nKey="appeal->>AppealNotes.CandidateAppealNotesNote" />
                </Typography>
              </div>
              <Divider className={classes.divider} />
              <SectionHeader
                className={classes.sectionHeader}
                title={t('appeal->>AppealNotes.Get Started')}
              />
              <Grid className={classes.examLevelSelect} xs={12}>
                <CustomSelect
                  label={t('appeal->>AppealNotes.Select Exam Level')}
                  placeholder="Select Exam Level"
                  displayEmpty
                  labelId="inputSelectLabel-examLevel"
                  id="inputSelect-examLevel"
                  onChange={(e)=>handleSelectChange(e)}
                  menuItems={examLevelMenuOptions(availableExamLevelResults)}
                  value={selectedExamLevelIndex}
                  variant="filled"
                />
              </Grid>
            </Grid>
            <Grid className={classes.startAppealButton} xs={12} md={4}>
              <ApiButton
                status={status}
                fullWidth
                id="button-appealImportantNotes-start"
                onClick={handleClick}
              >
                {t('appeal->>AppealNotes.StartANewAppeal')}
              </ApiButton>
            </Grid>
          </ContentTemplate>
        )}
    </>
  );
};

export default Notes;
